import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import AdminLoading from './AdminLoading';

const SPEED_DURATION = { slow: '40s', normal: '24s', fast: '12s' };

function LivePreview({ form }) {
  const duration = SPEED_DURATION[form.speed] || '24s';
  if (!form.text) {
    return (
      <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">Enter announcement text to preview</Typography>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: '100%', height: 36, bgcolor: form.bg_color || '#1b1b1b',
        borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex', whiteSpace: 'nowrap', width: 'max-content',
          animation: `ann-preview ${duration} linear infinite`,
          '@keyframes ann-preview': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(-33.333%)' },
          },
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            component="span"
            sx={{ px: '4rem', fontSize: '0.8rem', fontWeight: 500, letterSpacing: '0.04em', color: form.text_color || '#fff', lineHeight: '36px' }}
          >
            {form.text}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function AdminAnnouncementBar() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    enabled: false,
    text: '',
    bg_color: '#1b1b1b',
    text_color: '#ffffff',
    link: '',
    speed: 'normal',
  });

  useEffect(() => {
    api('/api/admin/announcement')
      .then((data) => {
        setForm({
          enabled:    !!data.enabled,
          text:       data.text || '',
          bg_color:   data.bg_color || '#1b1b1b',
          text_color: data.text_color || '#ffffff',
          link:       data.link || '',
          speed:      data.speed || 'normal',
        });
      })
      .catch((e) => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await api('/api/admin/announcement', { method: 'PUT', body: JSON.stringify(form) });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Card>
      <CardHeader
        title="Announcement Bar"
        subheader="A scrolling marquee strip shown at the very top of every storefront page. Use it for promotions, sale alerts, shipping notices, etc."
        action={
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                color="success"
              />
            }
            label={form.enabled ? 'Live' : 'Hidden'}
            labelPlacement="start"
          />
        }
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {saved && <Alert severity="success" sx={{ mb: 2 }}>Saved! Changes appear on the site within 60 seconds.</Alert>}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Live preview</Typography>
        <LivePreview form={form} />

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 680 }}>
          <TextField
            label="Announcement text"
            value={form.text}
            onChange={set('text')}
            fullWidth
            multiline
            minRows={2}
            placeholder="Free shipping on orders above ₹999 · Festival collection now live · Rent & return within 7 days"
            helperText="Separate multiple messages with · or | — the text scrolls continuously"
          />

          <TextField
            label="Link (optional)"
            value={form.link}
            onChange={set('link')}
            fullWidth
            size="small"
            placeholder="/shop or https://…"
            helperText="Makes the announcement clickable. Leave blank for no link."
          />

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Background colour</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={form.bg_color}
                  onChange={(e) => setForm((f) => ({ ...f, bg_color: e.target.value }))}
                  style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                />
                <TextField size="small" value={form.bg_color} onChange={set('bg_color')} sx={{ width: 110 }} inputProps={{ maxLength: 7 }} />
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Text colour</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={form.text_color}
                  onChange={(e) => setForm((f) => ({ ...f, text_color: e.target.value }))}
                  style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                />
                <TextField size="small" value={form.text_color} onChange={set('text_color')} sx={{ width: 110 }} inputProps={{ maxLength: 7 }} />
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Scroll speed</Typography>
              <ToggleButtonGroup
                value={form.speed}
                exclusive
                onChange={(_, v) => v && setForm((f) => ({ ...f, speed: v }))}
                size="small"
              >
                <ToggleButton value="slow">Slow</ToggleButton>
                <ToggleButton value="normal">Normal</ToggleButton>
                <ToggleButton value="fast">Fast</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <Box sx={{ pt: 1 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>
              {saving ? 'Saving…' : 'Save announcement bar'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
